import {
  AloftDateJSON,
  CompType,
  ListingType,
  PropertyData,
  ToolkitListingID,
} from '@aloft/toolkit-shared';

import { useAutofillProperty } from '../../../hooks/useAutofillProperty.ts';
import { CompSelectionState } from '../types.ts';
import { listingToPropertyData } from './listingToPropertyData.ts';

export const handleAddClickForCompCreation = async ({
  isLoading,
  rowSelection,
  createComps,
  autofillProperty,
  effectiveDate,
  orderID,
  isInspectifyOrder,
  hasArvOption,
  listingType,
}: {
  isLoading: boolean;
  rowSelection: CompSelectionState;
  createComps: (comps: PropertyData[]) => void;
  autofillProperty: ReturnType<typeof useAutofillProperty>;
  effectiveDate: AloftDateJSON;
  orderID: string;
  isInspectifyOrder: boolean;
  hasArvOption: boolean;
  listingType?: ListingType;
}) => {
  const listingToProp = listingToPropertyData(
    autofillProperty,
    effectiveDate,
    orderID,
    listingType,
  );

  // Note: Our query only returns properties of the same type as the subject, so using the subject's property type for all comps
  if (!isLoading) {
    if (isInspectifyOrder && hasArvOption) {
      // New behavior: create separate comps for each selected comp type (AIV/ARV)
      const properties: Promise<PropertyData>[] = Object.values(rowSelection)
        .filter(
          (c) => c.listingData && (c.listingData?.ToolkitListingID || c.listingData?.ListingId),
        )
        .map((c) => ({
          listing: c.listingData,
          id: c.listingData?.ToolkitListingID || (c.listingData?.ListingId as ToolkitListingID),
        }))
        .filter(({ id }) => {
          const selection = rowSelection[id];
          return selection && (selection.aiv || selection.arv);
        })
        .flatMap(({ id, listing }) => {
          const selection = rowSelection[id];
          if (!selection) return [];

          const compTypes: CompType[] = [];
          if (selection.aiv) compTypes.push('aiv');
          if (selection.arv) compTypes.push('arv');

          // Create a separate PropertyData for each comp type selected
          return compTypes.map(async (compType) => {
            const property = await listingToProp(listing!);
            return { ...property, compType };
          });
        });

      createComps(await Promise.all(properties));
    } else {
      // Old behavior: create comps without compType for non-Inspectify orders or Inspectify orders without ARV option
      const properties: Promise<PropertyData>[] = Object.values(rowSelection)
        .filter(
          (c) => c.listingData && (c.listingData?.ToolkitListingID || c.listingData?.ListingId),
        )
        .map((c) => ({
          listing: c.listingData,
          id: c.listingData?.ToolkitListingID || (c.listingData?.ListingId as ToolkitListingID),
        }))
        .map(async ({ listing }) => listingToProp(listing!));

      createComps(await Promise.all(properties));
    }
  }
};
